import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface GalleryImage {
  id: string;
  url: string;
  alt: string;
  title: string;
}

interface GalleryData {
  title: string;
  subtitle: string;
  images: GalleryImage[];
}

interface GalleryMobileProps {
  data: GalleryData;
}

const GalleryMobile: React.FC<GalleryMobileProps> = ({ data }) => {
  const [selectedImage, setSelectedImage] = useState<GalleryImage | null>(null);

  // Close on ESC
  useEffect(() => {
    const handler = (e: KeyboardEvent) =>
      e.key === 'Escape' && setSelectedImage(null);
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  return (
    <section className="py-10 sm:py-14 md:py-16 bg-white dark:bg-custom-dark">
      {/* Header */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center mb-8 sm:mb-12">
        <h2 className="text-xl sm:text-2xl md:text-4xl font-display font-bold text-gray-900 dark:text-white">
          {data.title}
        </h2>
        <p className="mt-2 text-sm sm:text-base md:text-lg text-gray-600 dark:text-gray-300">
          {data.subtitle}
        </p>
      </div>

      {/* Responsive Grid */}
      <div className="max-w-7xl mx-auto px-3 sm:px-6">
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-3 md:gap-4">
          {data.images.map((image, index) => (
            <motion.div
              key={image.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              onClick={() => setSelectedImage(image)}
              className="relative aspect-square overflow-hidden rounded-lg sm:rounded-xl shadow-lg cursor-pointer group"
            >
              <img
                src={image.url}
                alt={image.alt}
                loading="lazy"
                decoding="async"
                className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-110"
              />
            </motion.div>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedImage(null)}
          >
            <motion.div
              onClick={(e) => e.stopPropagation()}
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 280, damping: 26 }}
              className="relative max-w-2xl w-full flex flex-col items-center"
            >
              <div className="relative inline-block">
                <img
                  src={selectedImage.url}
                  alt={selectedImage.alt}
                  className="max-h-[75vh] object-contain rounded-xl shadow-2xl"
                />
                
                <button
                  className="absolute top-4 right-4 bg-black/50 hover:bg-black/70 rounded-full p-2 transition-colors z-10"
                  onClick={() => setSelectedImage(null)}
                  aria-label="Close"
                >
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default GalleryMobile;
